<template>
  <div :id="`${source}_status_tag_provider`" :class="[{ 'px-3': row }]">
    <div
      v-if="variant === variants.VARIANT1"
      class="flex flex-row gap-1 items-center rounded-3xl pl-[6px] pr-2 py-[3px] w-fit"
      :style="variantStyles"
    >
      <ThemeImage :icon="imageNames.WHITE_DOT" class="size-3" alt="white_dot" />
      <span class="text-[11px] font-semibold text-white break-normal">
        {{ statusText }}
      </span>
    </div>

    <div
      v-else-if="variant === variants.VARIANT2"
      class="flex flex-row gap-1 items-center rounded-3xl px-[10px] py-[6px]"
      :style="variantStyles"
    >
      <span class="text-sm font-semibold text-white break-normal">
        {{ statusText }}
      </span>
    </div>

    <div
      v-else-if="variant === variants.VARIANT3"
      class="flex items-center rounded px-1 py-[2px] w-fit"
      :style="variantStyles"
    >
      <span class="text-[10px] font-medium break-normal">{{ statusText }}</span>
    </div>

    <div
      v-else-if="variant === variants.VARIANT4"
      class="flex items-center px-2 py-1 w-fit rounded-xl"
      :style="{
        backgroundColor: `color-mix(in srgb, ${variantStyles} 20%, transparent)`,
        border: `1px solid ${variantStyles}`,
      }"
    >
      <span
        class="text-[11px] font-medium break-normal"
        :style="{ color: variantStyles }"
      >
        {{ statusText }}
      </span>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { imageNames } from "@/src/constants/imageConstants";
import {
  statusTagProviderColors,
  statusTagProviderColorMapping,
  statusTextColorMapping,
  statusColorMap,
  variants,
  statusVariant3Config,
  statusVariant4Mapping,
} from "@/src/constants/customComponentConstants.js";
import ThemeImage from "@/src/components/ThemeImage.vue";

const props = defineProps({
  source: { type: String, default: "custom_table" },
  row: { type: Object, required: false },
  value: { type: String, required: false },
  text: { type: String, default: "Unknown" },
  color: {
    type: String,
    default: null,
    validator: (value) =>
      value === null || Object.values(statusTagProviderColors).includes(value),
  },
  variant: {
    type: String,
    default: variants.VARIANT1,
    validator: (value) => Object.values(variants).includes(value),
  },
});

const statusText = computed(() => props.value || props.text);

const variantStyles = computed(() => {
  const text = (statusText.value || "").toLowerCase().trim();

  if (
    props.variant === variants.VARIANT1 ||
    props.variant === variants.VARIANT2
  ) {
    const colorToUse = props.color || getColorFromText(statusText.value);
    return {
      backgroundColor: statusTagProviderColorMapping[colorToUse],
    };
  }

  if (props.variant === variants.VARIANT3) {
    const config = statusVariant3Config[text];
    if (!config) return {};
    return {
      backgroundColor: config.bgColor,
      border: `1px solid ${config.borderColor}`,
      color: config.textColor,
    };
  }

  if (props.variant === variants.VARIANT4) {
    const colorToUse = props.color || getColorFromText(statusText.value);
    return statusVariant4Mapping[colorToUse];
  }

  return {};
});

const getColorFromText = (text) => {
  try {
    const normalizedText = text.toLowerCase().trim();

    for (const [status, variations] of Object.entries(statusTextColorMapping)) {
      if (
        variations.some(
          (variant) =>
            variant.toLowerCase() === normalizedText ||
            variant.toLowerCase().replace(/[_-\s]/g, "") ===
              normalizedText.replace(/[_-\s]/g, ""),
        )
      ) {
        return statusColorMap[status];
      }
    }
    return statusTagProviderColors.GREY;
  } catch (error) {
    console.error("An error occurred while getting color from text: ", error);
  }
};
</script>
